// Infinite Scroll Component v2.0.0
// Restauration scroll par ancre (ResizeObserver) + cache async
// + détection de filtre par signature produits.
if (!customElements.get("infinite-scroll")) {
  class InfiniteScrollComponent extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasMorePages = true;
      this.loadedProductsCount = 0;
      this.totalProductsCount = 0;
      this.urlCheckInterval = null;
      this.lastCheckedUrl = null;
      this.lastProductsSignature = null;
      this.options = {
        productsContainer: ".product-grid",
        paginationSelector: ".pagination_wrapper",
        productSelector: ".product-grid__item",
        autoLoad: false,
        loadMoreText: "Voir plus",
        scrollThreshold: -100,
        storagePrefix: "InfiniteScroll_",
        loadingImageUrl: "https://upload.wikimedia.org/wikipedia/commons/9/92/Loading_icon_cropped.gif",
        showProgress: false,
        productCountSelector: ".mobile-facets__count",
      };
    }

    connectedCallback() {
      if (!window.location.pathname.includes("/collections/")) return;
      // On pilote nous-mêmes la restauration du scroll (sinon le navigateur la fait AVANT que
      // le HTML mis en cache soit réinjecté -> remonte en haut, ou coince en bas).
      if ("scrollRestoration" in history) history.scrollRestoration = "manual";
      // Parse attributes
      [
        ["auto-load", (val) => (this.options.autoLoad = val !== "false")],
        ["load-more-text", (val) => (this.options.loadMoreText = val)],
        ["threshold", (val) => (this.options.scrollThreshold = parseInt(val) || 0)],
        ["loading-image", (val) => (this.options.loadingImageUrl = val)],
        ["show-progress", (val) => (this.options.showProgress = val === "true")],
        ["product-count-selector", (val) => (this.options.productCountSelector = val)],
      ].forEach(([attributeName, setOption]) => this.hasAttribute(attributeName) && setOption(this.getAttribute(attributeName)));
      // Initialize state and UI (with cache restore on initial load)
      if (!this.initializeState(true)) return;
      this.setupUI();
      // Setup scroll event listener
      if (this.options.autoLoad) {
        // `_suppressAutoLoad` : pendant une restauration de scroll, sauter près du bas ne doit
        // PAS enchaîner plusieurs loadNextPage d'un coup (sinon freeze au retour).
        this.boundCheckScroll = () => !this._suppressAutoLoad && this.productsContainer?.getBoundingClientRect().bottom - this.options.scrollThreshold <= window.innerHeight && this.loadNextPage();
        window.addEventListener("scroll", this.boundCheckScroll);
      }
      // --- Sauvegarde de la position de scroll ---
      // 1) en continu pendant le scroll (throttlé via rAF) : LÉGER (juste la position en pixels).
      this.boundSaveScroll = () => {
        if (this._scrollSaveQueued) return;
        this._scrollSaveQueued = true;
        requestAnimationFrame(() => {
          this._scrollSaveQueued = false;
          this.saveScrollPosition();
        });
      };
      window.addEventListener("scroll", this.boundSaveScroll, { passive: true });
      // 2) au moment de QUITTER la page (clic produit, retour, swipe, passage en arrière-plan) :
      //    on calcule l'ancre. pagehide + visibilitychange couvrent desktop ET mobile/iOS.
      this.boundSaveOnHide = () => this.saveAnchor();
      window.addEventListener("pagehide", this.boundSaveOnHide);
      this.boundSaveOnHidden = () => {
        if (document.visibilityState === "hidden") this.saveAnchor();
      };
      document.addEventListener("visibilitychange", this.boundSaveOnHidden);
      // --- Restauration au retour : bfcache (persisted) ET reload classique (fréquent sur mobile) ---
      this.boundHandlePageShow = () => this.restoreScrollPosition();
      window.addEventListener("pageshow", this.boundHandlePageShow);
      this.restoreScrollPosition(); // reload classique : le HTML caché est déjà réinjecté
      // Start URL / products monitoring
      this.startUrlMonitoring();
    }

    initializeState(useCache = false) {
      // Re-query DOM elements
      this.productsContainer = document.querySelector(this.options.productsContainer);
      this.pagination = document.querySelector(this.options.paginationSelector);
      if (!this.productsContainer) return false;
      // Get total products count
      const productCountElement = document.querySelector(this.options.productCountSelector);
      // Extract the number from the product count text (e.g., "72 products" -> 72)
      if (productCountElement) {
        const text = productCountElement.textContent.trim();
        const match = text.match(/\d+/);
        this.totalProductsCount = match ? parseInt(match[0]) : 0;
      }
      // Restore cache or initialize fresh
      const cached = useCache && this.cache("get");
      if (cached?.url === window.location.href) {
        this.productsContainer.innerHTML = cached.html;
        this.currentPage = cached.currentPage;
        this.hasMorePages = cached.hasMorePages;
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      } else {
        this.currentPage = 1;
        this.hasMorePages = this.checkHasMorePages(document);
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      }
      // Track previous URL
      this.previousUrl = this.cache("get", "previousUrl") || null;
      if (this.previousUrl !== window.location.href) {
        this.previousUrl = window.location.href;
        this.cache("set", "previousUrl", window.location.href);
      }
      return true;
    }

    setupUI() {
      if (!this.pagination) return;
      this.pagination.style.display = "none";
      this.removeLoadingElements();
      if (!this.options.autoLoad && this.hasMorePages) {
        // Create wrapper and all child elements
        this.loadMoreWrapper = this.createElementAfter(this.productsContainer, "div", { class: "infinite-scroll-load-more-wrapper" });
        if (this.options.showProgress) {
          this.productCountText = this.createAndAppend(this.loadMoreWrapper, "p", { class: "infinite-scroll-product-count" });
          this.progressBarBackground = this.createAndAppend(this.loadMoreWrapper, "div", { class: "infinite-scroll-progress-bar-background" });
          this.progressBar = this.createAndAppend(this.progressBarBackground, "div", { class: "infinite-scroll-progress-bar" });
        }
        this.loadMoreBtn = this.createAndAppend(this.loadMoreWrapper, "button", { class: "infinite-scroll-load-more eyebrow", text: this.options.loadMoreText, onclick: () => this.loadNextPage() });
        this.updateLoadingState(false);
      }
      if (this.options.loadingImageUrl) {
        this.loadingImgContainer = this.createElementAfter(this.loadMoreWrapper || this.productsContainer, "div", { class: "infinite-scroll-loading-image-container" });
        this.loadingImg = this.createAndAppend(this.loadingImgContainer, "img", { class: "infinite-scroll-loading-image", src: this.options.loadingImageUrl, alt: "Loading image", style: "display:none;" });
      }
    }

    getProducts() {
      return this.productsContainer ? Array.from(this.productsContainer.querySelectorAll(this.options.productSelector)) : [];
    }

    saveScrollPosition() {
      // Pendant le scroll : LÉGER (juste la position en pixels).
      this.cache("set", { scrollY: window.scrollY, scrollUrl: window.location.href });
    }

    saveAnchor() {
      // Au moment de quitter : on mémorise QUEL produit était en haut + son décalage (l'ancre),
      // au lieu d'un pixel figé qui dérive quand les images au-dessus se chargent.
      const products = this.getProducts();
      let anchorIndex = -1,
        anchorOffset = 0;
      for (let i = 0; i < products.length; i++) {
        const rect = products[i].getBoundingClientRect();
        if (rect.bottom > 0) {
          anchorIndex = i;
          anchorOffset = Math.round(rect.top);
          break;
        } // 1er produit visible
      }
      this.cache("set", { scrollUrl: window.location.href, scrollY: window.scrollY, anchorIndex, anchorOffset });
    }

    restoreScrollPosition() {
      const savedUrl = this.cache("get", "scrollUrl");
      if (savedUrl !== window.location.href) return; // pas la même collection -> on ne touche à rien
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorOffset = this.cache("get", "anchorOffset") || 0;
      const fallbackY = parseInt(this.cache("get", "scrollY"), 10);
      const hasAnchor = Number.isFinite(anchorIndex) && anchorIndex >= 0;
      if (!hasAnchor && (!Number.isFinite(fallbackY) || fallbackY <= 0)) return;

      // Pause de l'autoload pendant la correction (évite la cascade de loadNextPage au retour).
      this._suppressAutoLoad = true;
      let stopped = false,
        ro = null,
        stopTimer = null;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        this._suppressAutoLoad = false;
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchmove", onUserScroll);
        window.removeEventListener("keydown", onUserScroll);
        if (ro) ro.disconnect();
        clearTimeout(stopTimer);
      };
      const onUserScroll = () => stop(); // un vrai geste utilisateur annule la restauration

      // Cible RECALCULÉE à chaque correction d'après la position réelle du produit-ancre :
      // si du contenu au-dessus grandit et le pousse, on le SUIT (plus de dérive après 0.5-1s).
      const computeTarget = () => {
        const products = this.getProducts();
        if (hasAnchor && products[anchorIndex]) {
          return products[anchorIndex].getBoundingClientRect().top + window.scrollY - anchorOffset;
        }
        return fallbackY;
      };
      const apply = () => {
        if (stopped) return;
        const target = Math.max(0, computeTarget());
        const maxReach = document.documentElement.scrollHeight - window.innerHeight;
        // Garde-fou : on ne scrolle que si la page est assez haute (contenu réellement présent).
        if (maxReach >= target - 4 && Math.abs(window.scrollY - target) > 2) window.scrollTo(0, target);
      };

      // 1) ResizeObserver : re-corrige dès que la grille change de hauteur (images qui chargent).
      if (window.ResizeObserver && this.productsContainer) {
        ro = new ResizeObserver(() => apply());
        ro.observe(this.productsContainer);
      }
      // 2) Boucle rAF de secours (~1.2s) : capte aussi les décalages HORS grille (bannière, etc.).
      let frames = 0;
      const tick = () => {
        if (stopped) return;
        apply();
        if (++frames < 75) requestAnimationFrame(tick);
      };
      // 3) Filet final quand toutes les images sont chargées.
      window.addEventListener("load", () => apply(), { once: true });
      window.addEventListener("wheel", onUserScroll, { passive: true, once: true });
      window.addEventListener("touchmove", onUserScroll, { passive: true, once: true });
      window.addEventListener("keydown", onUserScroll, { once: true });
      stopTimer = setTimeout(stop, 2500); // borne dure : on arrête de corriger après 2.5s
      apply();
      requestAnimationFrame(tick);
    }

    scheduleHtmlCache() {
      // On sort la grosse sérialisation innerHTML + écriture sessionStorage du chemin critique
      // de rendu : elle s'exécute quand le thread est libre, pas pendant l'apparition des cartes.
      const save = () => this.cache("set", { html: this.productsContainer.innerHTML, currentPage: this.currentPage, hasMorePages: this.hasMorePages, url: window.location.href });
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
      const cancel = window.cancelIdleCallback || clearTimeout;
      if (this._cacheJob) cancel(this._cacheJob);
      this._cacheJob = idle(save);
    }

    createElementAfter(ref, tag, attrs) {
      // Create element after reference and set attributes
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => (key === "text" ? (el.textContent = value) : key === "onclick" ? el.addEventListener("click", value) : el.setAttribute(key, value)));
      ref.insertAdjacentElement("afterend", el);
      return el;
    }

    createAndAppend(parent, tag, attrs) {
      // Create element after parent and append to parent
      const el = this.createElementAfter(parent, tag, attrs);
      parent.appendChild(el);
      return el;
    }

    removeLoadingElements() {
      document.querySelectorAll(".infinite-scroll-load-more-wrapper, .infinite-scroll-load-more, .infinite-scroll-loading-image-container, .infinite-scroll-loading-image").forEach((el) => el.remove());
      // Reset load more wrapper, button and loading image
      this.loadMoreWrapper = this.loadMoreBtn = this.loadingImgContainer = this.loadingImg = null;
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasMorePages) return;
      this.isLoading = true;
      this.updateLoadingState(true);
      // Load next page
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("page", this.currentPage + 1);
        // Fetch next page
        const res = await fetch(url.toString(), { credentials: "same-origin" });
        if (!res.ok) return this.handleEndOfResults();
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        // Get container and check if it has children
        const container = doc.querySelector(this.options.productsContainer);
        if (!container?.children.length) return this.handleEndOfResults();
        // Clone children and append to products container
        Array.from(container.children).forEach((p) => this.productsContainer.appendChild(p.cloneNode(true)));
        // Update loaded products count and check if there are more pages
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
        this.currentPage++;
        this.hasMorePages = this.checkHasMorePages(doc);
        // Check if there are more pages and cache data (écriture asynchrone, hors rendu)
        if (!this.hasMorePages) this.handleEndOfResults();
        this.scheduleHtmlCache();
        // IMPORTANT : on resynchronise la signature APRÈS notre propre ajout, pour que le
        // monitoring ne confonde pas "on a chargé une page" avec "un filtre a changé la grille".
        this.lastProductsSignature = this.getProductsSignature();
      } catch {
        // Handle end of results
        this.handleEndOfResults();
      } finally {
        // Update loading state
        this.isLoading = false;
        this.updateLoadingState(false);
      }
    }

    checkHasMorePages(doc) {
      // Get container and check if it has children and if there is a next page
      const container = doc.querySelector(this.options.productsContainer);
      return container?.children.length > 0 && !!doc.querySelector(`a[href*="page=${this.currentPage + 1}"], .pagination a[rel="next"], .pagination .next:not(.disabled)`);
    }

    updateLoadingState(isLoading) {
      // Update load more button and wrapper and loading image
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
      if (this.loadMoreWrapper) this.loadMoreWrapper.style.display = isLoading ? "none" : "flex";
      if (this.loadingImgContainer) this.loadingImgContainer.style.display = isLoading ? "flex" : "none";
      if (this.loadingImg) this.loadingImg.style.display = isLoading ? "block" : "none";
      // Update product count text and progress bar
      if (this.productCountText && this.totalProductsCount > 0) this.productCountText.textContent = `${this.loadedProductsCount} products loaded out of ${this.totalProductsCount}`;
      if (this.progressBar && this.totalProductsCount > 0) this.progressBar.style.width = `${(this.loadedProductsCount / this.totalProductsCount) * 100}%`;
    }

    handleEndOfResults() {
      this.hasMorePages = false;
      this.removeLoadingElements();
    }

    // Cache controller
    cache(action, key, value) {
      const formatKey = (s) => this.options.storagePrefix + s;
      const serialize = (val) => (typeof val === "object" ? JSON.stringify(val) : String(val));
      const deserialize = (val) => {
        try {
          return val ? JSON.parse(val) : null;
        } catch {
          return val;
        }
      };
      try {
        if (action === "get") {
          if (!key) {
            // Get all values from session storage
            const vals = Object.fromEntries(["url", "html", "hasMorePages", "currentPage", "scrollY", "scrollUrl", "previousUrl"].map((k) => [k, deserialize(sessionStorage.getItem(formatKey(k)))]));
            return vals.url && vals.html ? vals : null;
          }
          // Get specific value from session storage
          return deserialize(sessionStorage.getItem(formatKey(key)));
        }
        if (action === "set") {
          // Set specific value or multiple values from session storage
          if (typeof key === "object") Object.entries(key).forEach(([k, val]) => sessionStorage.setItem(formatKey(k), serialize(val)));
          else sessionStorage.setItem(formatKey(key), serialize(value));
        }
        // Clear all values from session storage
        if (action === "clear")
          Object.keys(sessionStorage)
            .filter((k) => k.startsWith(this.options.storagePrefix))
            .forEach((k) => sessionStorage.removeItem(k));
      } catch {}
      return null;
    }

    // --- Monitoring filtres / collection ---
    startUrlMonitoring() {
      this.lastCheckedUrl = window.location.href;
      this.lastProductsSignature = this.getProductsSignature();
      this.urlCheckInterval = setInterval(() => this.checkForChanges(), 250);
    }

    getProductsSignature() {
      // Signature LÉGÈRE de la grille : nb de produits + identité du 1er et du dernier produit.
      // - nos propres chargements changent le dernier + le nombre -> on resynchronise après coup,
      //   donc ça ne déclenche PAS de reset.
      // - un filtre/collection remplace la grille (1er produit et/ou nb différents) -> détecté,
      //   même dès la page 1 et sans dépendre du cache.
      const products = this.productsContainer ? this.productsContainer.querySelectorAll(this.options.productSelector) : [];
      if (!products.length) return "0";
      const idOf = (el) => el.querySelector('a[href*="/products/"]')?.getAttribute("href") || el.getAttribute("data-product-id") || el.id || "";
      return products.length + "|" + idOf(products[0]) + "|" + idOf(products[products.length - 1]);
    }

    checkForChanges() {
      const currentUrl = window.location.href;
      const currentSignature = this.getProductsSignature();
      const urlChanged = currentUrl !== this.lastCheckedUrl;
      // On resynchronise après CHAQUE chargement de page (voir loadNextPage), donc une signature
      // différente ici = changement EXTERNE = filtre/collection appliqué par Shopify.
      const productsChanged = currentSignature !== this.lastProductsSignature;

      if (productsChanged) {
        if (window.location.pathname.includes("/collections/")) this.handleFilterChange();
        this.lastCheckedUrl = currentUrl;
        this.lastProductsSignature = this.getProductsSignature(); // grille fraîche après reinit
      } else if (urlChanged) {
        // URL changée mais produits identiques (param cosmétique) : on resync sans reset destructif.
        this.lastCheckedUrl = currentUrl;
      }
    }

    handleFilterChange() {
      // Le contenu a changé (filtre/collection). On repart proprement : Shopify gère déjà la grille.
      this.cache("clear"); // efface aussi la position de scroll mémorisée (on ne restaure pas un vieux scroll)
      this.isLoading = false;
      this.initializeState(false);
      this.setupUI();
    }

    disconnectedCallback() {
      if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
      if (this.boundSaveScroll) window.removeEventListener("scroll", this.boundSaveScroll);
      if (this.boundSaveOnHide) window.removeEventListener("pagehide", this.boundSaveOnHide);
      if (this.boundSaveOnHidden) document.removeEventListener("visibilitychange", this.boundSaveOnHidden);
      if (this.boundHandlePageShow) window.removeEventListener("pageshow", this.boundHandlePageShow);
      if (this._cacheJob) (window.cancelIdleCallback || clearTimeout)(this._cacheJob);
      if (this.urlCheckInterval) clearInterval(this.urlCheckInterval);
    }
  }
  customElements.define("infinite-scroll", InfiniteScrollComponent);
}
